<template>
    <div>
        <div v-if="nextInvoice.date || issuedInvoices.length > 0">
            <div v-if="credits > 0.0" class="mb-6">
                <div class="bg-gray-200 border-l-4 border-info-500 p-4 rounded shadow w-full" role="alert">
                    <p class="mb-0" v-html="$t('organization_payments.credits_info', {
                        emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                        credits: credits,
                        emphasizedClose: '</span>',
                    })"></p>
                </div>
            </div>
            <div class="mb-3">
                <div class="mb-2">
                    <div class="font-bold text-sm">{{ $t('organization_payments.next_invoice') }}</div>
                    <p v-if="nextInvoice.date" class="help-block my-0" v-html="$t('organization_payments.next_invoice_info', {
                        emphasizedOpen: '<span class=&quot;font-semibold&quot;>',
                        date: formatDate(nextInvoice.date),
                        emphasizedClose: '</span>',
                    })"></p>
                </div>
                <div v-if="nextInvoice.date" class="row">
                    <div class="col-xs-12">
                        <div class="table-responsive">
                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{{ $t('organization_payments.description') }}</th>
                                        <th>{{ $t('organization_payments.period') }}</th>
                                        <th class="text-right">{{ $t('organization_payments.price') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(product, index) in nextInvoice.products">
                                        <th scope="row">{{ index + 1 }}</th>
                                        <td>{{ product.description }}</td>
                                        <td>{{ formatDate(product.started_at) + ' – ' + formatDate(product.ended_at) }}</td>
                                        <td class="text-right">&euro;{{ product.total.toFixed(2) }}</td>
                                    </tr>
                                    <tr v-for="(adjustment, index) in nextInvoice.adjustments">
                                        <th scope="row">{{ index + 1 + nextInvoice.products.length }}</th>
                                        <td>{{ adjustment.description }}</td>
                                        <td>{{ $t('globals.n_a') }}</td>
                                        <td class="text-right">- &euro;{{ (adjustment.total * (-1)).toFixed(2) }}</td>
                                    </tr>
                                    <tr>
                                        <th scope="row" colspan="3">
                                            <div class="text-right">
                                                {{ $t('organization_payments.total').toUpperCase() }}
                                            </div>
                                        </th>
                                        <td class="text-right">
                                            <strong>&euro;{{ nextInvoiceTotal.toFixed(2) }}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div v-else class="bg-gray-200 border-l-4 border-warning-500 mb-6 md:w-4/5 mx-auto p-4 rounded shadow w-full" role="alert">
                    <p class="mb-0">{{ $t('organization_payments.no_next_invoice') }}</p>
                </div>
            </div>
            <div>
                <div class="mb-2">
                    <div class="font-bold text-sm">{{ $t('organization_payments.issued_invoices') }}</div>
                </div>
                <div v-if="issuedInvoices.length > 0" class="row">
                    <div class="col-xs-12">
                        <div class="table-responsive">
                            <table class="table table-striped">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>{{ $t('globals.invoice') }}</th>
                                        <th>{{ $t('globals.date') }}</th>
                                        <th class="text-left">{{ $t('organization_payments.paid') }}</th>
                                        <th class="text-right">{{ $t('organization_payments.total') }}</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(invoice, index) in issuedInvoices">
                                        <td>{{ index + 1 }}</td>
                                        <td>{{ invoice.identificator }}</td>
                                        <td>{{ formatDate(invoice.created_at) }}</td>
                                        <td class="text-left">
                                            <div class="inline-flex items-center">
                                                <template v-if="invoice.status === 'pending'">
                                                    <div style="padding: 0 1px;">
                                                        <span class="icon icon-refresh icon-lg icon-spin mr-2 text-warning-500"></span>
                                                    </div>
                                                </template>
                                                <template v-else-if="invoice.status === 'paid'">
                                                    <div style="padding: 0px;">
                                                        <span class="icon icon-check icon-lg mr-2 text-primary-500"></span>
                                                    </div>
                                                </template>
                                                <template v-else-if="['unpaid', 'payment_failed', 'canceled'].includes(invoice.status)">
                                                    <div style="padding: 0 2px;">
                                                        <span class="icon icon-times icon-lg mr-2 text-danger-500"></span>
                                                    </div>
                                                </template>
                                                <template v-if="invoice.status === 'pending'">
                                                    <span class="text-warning-600">{{ invoiceStatuses[invoice.status] }}</span>
                                                </template>
                                                <template v-else-if="invoice.status === 'paid'">
                                                    <span class="text-primary-600">{{ invoiceStatuses[invoice.status] }}</span>
                                                </template>
                                                <template v-else-if="['unpaid', 'payment_failed', 'canceled'].includes(invoice.status)">
                                                    <span class="text-danger-600">{{ invoiceStatuses[invoice.status] }}</span>
                                                </template>
                                            </div>
                                        </td>
                                        <td class="text-right">&euro;{{ invoice.total.toFixed(2) }}</td>
                                        <td class="text-right">
                                            <div class="inline-flex">
                                                <template v-if="['unpaid', 'payment_failed'].includes(invoice.status)">
                                                    <button v-if="! paymentFormIsDisabled" @click="collectPayment(invoice)" class="btn btn-primary btn-xs mr-2">
                                                        {{ $t('organization_payments.pay') }}
                                                    </button>
                                                    <div v-else class="btn btn-primary btn-xs disabled mr-2" v-tooltip="{ content: $t('organization_payments.invoice_payment_button_disabled_info', { lineBreak: '<br/>' }), placement: 'top' }">
                                                        {{ $t('organization_payments.pay') }}
                                                        <span class="icon icon-question-circle icon-fw"></span>
                                                    </div>
                                                </template>
                                                <a :href="`/organization/payments/invoice/${invoice.id}/download`" class="btn btn-primary btn-xs">
                                                    {{ $t('globals.download') }}
                                                </a>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <div v-else class="bg-gray-200 border-l-4 border-warning-500 mb-6 md:w-4/5 mx-auto p-4 rounded shadow w-full" role="alert">
                    <p class="mb-0">{{ $t('organization_payments.no_issued_invoices') }}</p>
                </div>
            </div>
        </div>
        <div v-else class="flex flex-col items-center justify-center w-full">
            <div class="md:w-1/4 pb-8 w-1/2">
                <svg-printing-invoices></svg-printing-invoices>
            </div>
            <div class="md:w-3/4 text-center w-full">
                <h5>{{ $t('organization_payments.no_invoices') }}</h5>
                <p>{{ $t('organization_payments.no_invoices_found_tip') }}</p>
            </div>
        </div>
    </div>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';
import SvgPrintingInvoices from '../../../svg/printing-invoices.vue';

export default {
    components: {
        'svg-printing-invoices': SvgPrintingInvoices,
    },

    props: {
        nextInvoice: {
            type: Object,
            required: true,
        },
        issuedInvoices: {
            type: Array,
            required: true,
        },
        invoiceStatuses: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            paymentFormIsDisabled: true,
        };
    },

    created() {
        const organization = window.Globals.user.organization;
        if (organization.bill_first_name && organization.bill_last_name && organization.bill_email && organization.bill_country_code && organization.bill_state && organization.bill_address &&
            ((organization.bill_company_name && organization.bill_tax_id && organization.bill_registration_number) || (!organization.bill_company_name && !organization.bill_tax_id && !organization.bill_registration_number)) &&
            ((organization.payment_processor === 'payu' && organization.cc_token_hash) || (organization.payment_processor === 'stripe' && organization.stripe_payment_method_id))) {
            this.paymentFormIsDisabled = false;
        }

        this.listenForStatusUpdates(window.Globals.user.organization.id);
    },

    computed: {
        nextInvoiceTotal() {
            const productsTotal = this.nextInvoice.products.reduce((total, product) => total + product.total, 0);
            const adjustmentsTotal = this.nextInvoice.adjustments.reduce((total, product) => total + product.total, 0);
            const nextInvoiceTotal = productsTotal + adjustmentsTotal;
            return nextInvoiceTotal;
        },

        credits() {
            return Number(parseFloat(window.Globals.user.organization.credits)).toFixed(2);
        },
    },

    methods: {
        collectPayment(invoice) {
            const form = new Form({
                invoice_id: invoice.id,
            });

            Globals.post('/api/v1/collect-invoice-payment', form)
                .then(() => Bus.$emit('shouldUpdateIssuedInvoice', {
                    id: form.invoice_id,
                    status: 'pending',
                    fiscalInvoiceId: null,
                }))
                .catch(() => {
                    const errors = form.errors.flatten();
                    const message = errors.length > 0 ? errors[0] : this.$t('globals.unexpected_error');
                    window.toastr.clear();
                    window.toastr.error(message);
                });
        },

        listenForStatusUpdates(organizationId) {
            window.Echo.private(`Admin.Organization.${organizationId}`)
                .listen('.invoice.status_updated', response => Bus.$emit('shouldUpdateIssuedInvoice', {
                    id: response.id,
                    status: response.status,
                    fiscalInvoiceId: response.fiscal_invoice_id,
                }));
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y');
        },
    },
};
</script>
